import React, { useState, Children, lazy, Component } from 'react';
import { Phone, HeartPulse, Brain, ShieldAlert, FileText, Download, ChevronDown, Globe, MapPin, Scale, Baby, Utensils, Award, HandHeart, Users2, Sparkles, Shield, Stethoscope, Home, LifeBuoy, Megaphone, Church, Heart, Wifi, Leaf, DollarSign, Star, Building2, Landmark, Info, Clock, AlertTriangle, Mail, ExternalLink } from 'lucide-react';
const formatPhoneNumber = (phone: string) => {
  const cleaned = phone.replace(/\D/g, '');
  return `+1${cleaned}`;
};
// Extrae el dominio de una URL para usar con servicios de favicon
const getDomain = (url?: string) => {
  if (!url) return null;
  try {
    const cleaned = url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    return cleaned;
  } catch {
    return null;
  }
};
// Genera iniciales para usar como fallback si el logo no carga
const getInitials = (name: string) => {
  const cleaned = name.replace(/\(.*?\)/g, '').replace(/—/g, '').trim();
  const words = cleaned.split(/\s+/).filter(w => w.length > 2 && !['del', 'and', 'the', 'for', 'of', 'de', 'la', 'el', 'los'].includes(w.toLowerCase()));
  return words.slice(0, 2).map(w => w[0]).join('').toUpperCase();
};
// Componente Visual del Logo Oficial — usa múltiples fuentes con fallback automático
const OrgLogo: React.FC<{
  name: string;
  website?: string;
  color: string;
  bgColor: string;
  photo?: string;
}> = ({
  name,
  website,
  color,
  bgColor,
  photo
}) => {
  const [sourceIdx, setSourceIdx] = useState(0);
  const domain = getDomain(website);
  const initials = getInitials(name);
  const logoSources = domain ? [`https://icons.duckduckgo.com/ip3/${domain}.ico`, `https://www.google.com/s2/favicons?domain=${domain}&sz=128`] : [];
  const currentSource = logoSources[sourceIdx];
  const showLogo = domain && sourceIdx < logoSources.length;
  // Si el contacto tiene una foto propia, se muestra a pantalla completa en el avatar.
  if (photo) {
    return <div className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-282" data-mp="2rwxzra0cloxb">
        <img src={photo} alt={`Foto de ${name}`} className="w-full h-full object-cover notranslate" loading="lazy" data-id="element-283" data-mp="31wotvakzy1wv" />
      </div>;
  }
  return <div className={`flex-shrink-0 w-14 h-14 md:w-16 md:h-16 bg-white rounded-2xl border border-gray-200 shadow-sm flex items-center justify-center overflow-hidden p-1.5`} data-id="element-284" data-mp="2473qf2f8miht">
      {showLogo ? <img src={currentSource} alt={`Logo de ${name}`} className="w-full h-full object-contain notranslate" onError={() => setSourceIdx(i => i + 1)} loading="lazy" data-id="element-285" data-mp="2ixufw3tcx5hr" /> : <div className={`w-full h-full ${bgColor} rounded-xl flex items-center justify-center`} data-id="element-286" data-mp="1qgi413qkrqf6">
          <span className={`font-black text-base md:text-lg ${color} notranslate tracking-tight`} data-id="element-287" data-mp="7lvpyrkt3958">
            {initials}
          </span>
        </div>}
    </div>;
};
interface Contact {
  name: string;
  phone?: string;
  website?: string;
  websiteLabel?: string;
  address?: string;
  service?: string;
  email?: string;
  contactPerson?: string;
  photo?: string;
}
interface Category {
  categoryEs: string;
  categoryEn: string;
  icon: any;
  color: string;
  bgColor: string;
  borderColor: string;
  contacts: Contact[];
  pending?: boolean;
  intro?: string;
  introEn?: string;
}
export function ResourceDirectory() {
  const [openCategory, setOpenCategory] = useState<number | null>(0);
  const directory: Category[] = [{
    categoryEs: 'Salud / Clínicas y Hospitales',
    categoryEn: 'Health / Clinics & Hospitals',
    icon: HeartPulse,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    contacts: [{
      name: 'Valley Community Healthcare',
      phone: '818-763-8836',
      website: 'https://valleycommunityhealthcare.org',
      websiteLabel: 'valleycommunityhealthcare.org',
      address: 'North Hollywood, CA',
      service: 'Clínica general comunitaria.'
    }, {
      name: 'El Proyecto del Barrio',
      phone: '818-810-3500',
      website: 'https://elproyecto.us',
      websiteLabel: 'elproyecto.us',
      address: '8932 Woodman Ave, Arleta, CA',
      service: 'Clínica comunitaria.'
    }, {
      name: 'Valley Presbyterian Hospital',
      phone: '818-782-6600',
      website: 'https://valleypres.org',
      websiteLabel: 'valleypres.org',
      address: 'Van Nuys, CA',
      service: 'Hospital.'
    }, {
      name: 'Olive View Medical Center',
      phone: '747-210-3000',
      website: 'https://dhs.lacounty.gov',
      websiteLabel: 'dhs.lacounty.gov',
      address: '14445 Olive View Dr, Sylmar, CA',
      service: 'Hospital público / Medi-Cal.'
    }, {
      name: 'Serra Medical Group',
      phone: '855-275-7377',
      website: 'https://serramedicalgroup.com/es/',
      websiteLabel: 'serramedicalgroup.com',
      address: 'North Hollywood / Panorama City / Sun Valley',
      service: 'Medi-Cal / Medicare.'
    }, {
      name: 'Tri-State Community Healthcare',
      phone: '818-895-3100',
      website: 'https://tristateclinics.org',
      websiteLabel: 'tristateclinics.org',
      address: '11333 Sepulveda Blvd, Mission Hills, CA',
      service: 'Clínica de bajo costo.'
    }, {
      name: 'San Fernando Community Health Center (SFCHC)',
      phone: '818-963-5690',
      website: 'https://sfchealthcenter.org',
      websiteLabel: 'sfchealthcenter.org',
      address: '732 Mott St, San Fernando, CA',
      service: 'Medi-Cal / bajo costo.'
    }, {
      name: 'Northeast Valley Health Corporation (NEVHC)',
      phone: '818-898-1388',
      website: 'https://nevhc.org',
      websiteLabel: 'nevhc.org',
      address: '1172 N Maclay Ave, San Fernando, CA',
      service: 'Medi-Cal.'
    }, {
      name: 'Northridge Hospital Medical Center',
      phone: '818-885-8500',
      website: 'https://dignityhealth.org',
      websiteLabel: 'dignityhealth.org',
      address: '18300 Roscoe Blvd, Northridge, CA',
      service: 'Hospital.'
    }, {
      name: 'City of Hope — Mission Hills',
      phone: '818-660-4700',
      website: 'https://cityofhope.org',
      websiteLabel: 'cityofhope.org',
      address: 'Mission Hills, CA',
      service: 'Atención especializada en cáncer.'
    }, {
      name: 'Comprehensive Community Health Centers (CCHC)',
      phone: '818-755-8000',
      website: 'https://cchccenters.org',
      websiteLabel: 'cchccenters.org',
      address: 'North Hollywood, CA',
      service: 'Clínica comunitaria.'
    }]
  }, {
    categoryEs: 'Salud Mental',
    categoryEn: 'Mental Health',
    icon: Brain,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    intro: 'El bienestar emocional es importante para toda la comunidad. Apoyo psicológico, consejería y servicios de salud mental en español.',
    introEn: 'Emotional wellbeing matters. Psychological support, counseling, and mental health services in Spanish.',
    contacts: [{
      name: 'ENKI Health & Research Systems',
      phone: '866-227-1302',
      service: 'Servicios integrales de salud mental con enfoque comunitario.'
    }, {
      name: 'Centro de Salud Mental San Fernando',
      phone: '818-832-2400',
      service: 'Atención especializada para la comunidad del Valle de San Fernando.'
    }, {
      name: 'Centro de Salud Mental Edelman Westside',
      phone: '310-966-6500',
      service: 'Servicios de salud mental para residentes del lado oeste de LA.'
    }, {
      name: 'Centro Amanecer Bienestar',
      phone: '213-481-7464',
      service: 'Apoyo emocional y consejería para personas y familias en el centro de LA.'
    }, {
      name: 'LACDMH — Salud Mental del Condado de LA',
      phone: '800-854-7771',
      website: 'https://dmh.lacounty.gov',
      websiteLabel: 'dmh.lacounty.gov',
      contactPerson: 'Magali Garcia',
      service: 'Línea oficial de salud mental del Condado de Los Ángeles.'
    }, {
      name: 'NAMI San Fernando Valley',
      phone: '818-994-6747',
      website: 'https://namisfv.org',
      websiteLabel: 'namisfv.org',
      email: 'martha.sobhani@namisfv.org',
      contactPerson: 'Martha Sobhani',
      address: 'San Fernando Valley',
      service: 'Apoyo en salud mental para toda la comunidad.'
    }, {
      name: 'Tarzana Treatment Centers',
      phone: '800-996-1051',
      website: 'https://tarzanatc.org',
      websiteLabel: 'tarzanatc.org',
      address: 'Tarzana / Northridge',
      service: 'Salud mental y tratamiento de adicciones.'
    }, {
      name: 'The Help Group',
      phone: '818-779-5198',
      website: 'https://thehelpgroup.org',
      websiteLabel: 'thehelpgroup.org',
      address: 'Sherman Oaks, CA',
      service: 'Salud mental y apoyo para autismo.'
    }, {
      name: 'Línea de Crisis de Salud Mental',
      phone: '988',
      service: 'Disponible 24/7 en español.'
    }]
  }, {
    categoryEs: 'Servicios de Inmigración / Legales',
    categoryEn: 'Immigration / Legal Services',
    icon: Scale,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    intro: 'Servicios confidenciales en español. Sin importar tu estatus migratorio, tienes derechos.',
    introEn: 'Confidential services in Spanish. Regardless of your immigration status, you have rights.',
    contacts: [{
      name: 'Bet Tzedek',
      phone: '323-939-0506',
      service: 'Ayuda legal para inmigrantes y familias.'
    }, {
      name: 'Centro de Políticas para Inmigrantes de California',
      phone: '213-250-0880',
      service: 'Apoyo migratorio.'
    }, {
      name: 'Coalición de Derechos de Inmigrantes de Long Beach',
      phone: '562-204-6333',
      service: 'Derechos migratorios.'
    }, {
      name: 'NDLON',
      phone: '626-799-3566',
      service: 'Derechos laborales y migratorios.'
    }, {
      name: 'Pomona Economic Opportunity Center',
      phone: '909-397-4215',
      service: 'Apoyo legal a trabajadores.'
    }, {
      name: 'Salva — Palmdale',
      phone: '661-274-9516',
      service: 'Servicios migratorios.'
    }, {
      name: 'Fundación de Asistencia Legal de Los Ángeles',
      phone: '800-399-4529',
      service: 'Asistencia legal.'
    }, {
      name: 'CHIRLA',
      phone: '888-624-4752',
      website: 'https://chirla.org',
      websiteLabel: 'chirla.org',
      service: 'Servicios de inmigración.'
    }, {
      name: 'Hermandad Mexicana Nacional',
      website: 'https://hermandadmexicananacional.com',
      websiteLabel: 'hermandadmexicananacional.com',
      service: 'Ciudadanía y trámites.'
    }, {
      name: 'CARECEN San Fernando Valley',
      phone: '818-989-7447',
      website: 'https://carecen-la.org',
      websiteLabel: 'carecen-la.org',
      address: 'Van Nuys, CA',
      service: 'Apoyo legal e inmigración.'
    }, {
      name: 'Refugee Children Center',
      phone: '818-279-6580',
      website: 'https://refugeechildrencenter.org',
      websiteLabel: 'refugeechildrencenter.org',
      address: 'San Fernando, CA',
      service: 'Apoyo para niños refugiados y familias.'
    }, {
      name: 'Child Care Resource Center (CCRC)',
      phone: '818-717-1000',
      website: 'https://ccrcca.org',
      websiteLabel: 'ccrcca.org',
      address: 'Chatsworth, CA',
      service: 'Recursos para cuidado infantil.'
    }]
  }, {
    categoryEs: 'Bancos de Alimentos / Asistencia Alimentaria',
    categoryEn: 'Food Banks / Food Assistance',
    icon: Utensils,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    intro: 'Alimentos frescos y nutritivos sin costo. Disponible para toda la comunidad sin importar su estatus migratorio.',
    introEn: 'Fresh, nutritious food at no cost. Available to the entire community regardless of immigration status.',
    contacts: [{
      name: 'Grace Resource Center Inc.',
      phone: '661-940-5272',
      address: '45134 Sierra Hwy, Lancaster, CA 93534',
      service: 'Banco de alimentos.'
    }, {
      name: 'Food Oasis',
      website: 'https://foodoasis.la',
      websiteLabel: 'foodoasis.la',
      service: 'Directorio en línea de recursos alimentarios en LA.'
    }, {
      name: 'Centro de Alivio',
      phone: '818-988-2931',
      address: '6615 Van Nuys Blvd, Van Nuys, CA 91405',
      service: 'Asistencia alimentaria.'
    }, {
      name: 'Banco Regional de Alimentos de Los Ángeles',
      phone: '323-234-3030',
      address: '1734 E 41st St, Los Ángeles, CA 90058',
      service: 'Banco regional de alimentos.'
    }, {
      name: 'MEND',
      phone: '818-897-2443',
      website: 'https://mendpoverty.org',
      websiteLabel: 'mendpoverty.org',
      address: 'Pacoima, CA',
      service: 'Banco de alimentos.'
    }, {
      name: 'North Valley Caring Services',
      phone: '818-891-0481',
      website: 'https://nvcsinc.org',
      websiteLabel: 'nvcsinc.org',
      address: 'North Hills, CA',
      service: 'Servicios básicos y alimentos.'
    }, {
      name: 'West Valley Food Pantry',
      phone: '818-346-5554',
      website: 'https://westvalleyfoodpantry.org',
      websiteLabel: 'westvalleyfoodpantry.org',
      address: 'Woodland Hills, CA',
      service: 'Banco de alimentos.'
    }, {
      name: 'Heart of the Canyons Church',
      phone: '661-297-9500',
      service: 'Banco de alimentos.'
    }]
  }, {
    categoryEs: 'Servicios Sociales',
    categoryEn: 'Social Services',
    icon: HandHeart,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    intro: 'Beneficios, asistencia económica y apoyo integral para mejorar la calidad de vida.',
    introEn: 'Benefits, economic assistance, and comprehensive support to improve quality of life.',
    contacts: [{
      name: 'Mexican American Opportunity Foundation (MAOF)',
      phone: '323-890-1555',
      service: 'Programas de empleo, educación y servicios sociales para la comunidad latina.'
    }, {
      name: 'DPSS — Servicios Sociales del Condado',
      phone: '866-611-6976',
      service: 'CalFresh, Medi-Cal y otros beneficios para residentes y familias.'
    }, {
      name: 'Oficina de Enlace para Personas Mayores',
      phone: '213-738-2065',
      service: 'Conexión con servicios especializados para personas mayores, familias y cuidadores.'
    }]
  }, {
    categoryEs: 'Centros Comunitarios y para Personas Mayores',
    categoryEn: 'Community & Senior Centers',
    icon: Users2,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    intro: 'Espacios seguros para socializar y acceder a programas recreativos, educativos y de bienestar. Ofrecen comidas, transporte y actividades para todas las edades.',
    introEn: 'Safe spaces for socializing and accessing recreation, education, and wellness programs. Meals, transportation, and activities for all ages.',
    contacts: [{
      name: 'South El Monte Senior Center',
      phone: '626-448-0131',
      service: 'Centro comunitario para personas mayores y todas las edades.'
    }, {
      name: 'Culver City Senior Center',
      phone: '310-253-6700',
      service: 'Centro comunitario para personas mayores y todas las edades.'
    }, {
      name: 'Centro Eastmont',
      phone: '323-263-0446',
      service: 'Centro comunitario para personas mayores y todas las edades.'
    }, {
      name: 'Centro Pasadena',
      phone: '626-795-4331',
      service: 'Centro comunitario para personas mayores y todas las edades.'
    }, {
      name: 'Programas del Condado de Los Ángeles',
      phone: '626-579-2043',
      service: 'Programas del condado para residentes.'
    }]
  }, {
    categoryEs: 'Centros de Bienestar Comunitario',
    categoryEn: 'Community Wellness Centers',
    icon: Sparkles,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    intro: 'Clínicas y centros enfocados en el bienestar integral físico, mental y social.',
    introEn: 'Clinics and centers focused on integral physical, mental, and social wellbeing.',
    contacts: [{
      name: 'Centro de Salud MLK',
      phone: '310-295-8141',
      service: 'Atención integral de salud para la comunidad en el sur de LA.'
    }, {
      name: 'Venice Family Clinic',
      phone: '310-392-8636',
      service: 'Servicios de salud accesibles para residentes de la zona costera.'
    }, {
      name: 'Clínica Monseñor Romero',
      phone: '213-989-7700',
      service: 'Salud comunitaria con enfoque en poblaciones vulnerables y familias.'
    }, {
      name: 'Antelope Valley Wellness Community',
      phone: '661-726-2850',
      service: 'Bienestar integral para residentes del Valle de Antelope.'
    }]
  }, {
    categoryEs: 'Violencia Doméstica',
    categoryEn: 'Domestic Violence',
    icon: Shield,
    color: 'text-pink-700',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    intro: '🚨 En caso de emergencia inmediata, llama al 911. Apoyo confidencial, refugio y orientación legal.',
    introEn: '🚨 In immediate emergency, call 911. Confidential support, shelter, and legal guidance.',
    contacts: [{
      name: 'Centro de Mujeres del Este de Los Ángeles',
      phone: '323-526-5819',
      service: 'Apoyo, refugio y servicios legales para personas y familias en situación de violencia.'
    }, {
      name: 'Paz Sobre Violencia',
      phone: '213-626-3393',
      service: 'Línea de crisis y servicios de apoyo para sobrevivientes.'
    }, {
      name: 'Amanecer Counseling',
      phone: '213-416-1106',
      service: 'Consejería especializada para personas y familias afectadas por violencia doméstica.'
    }, {
      name: 'Línea de Abuso a Adultos Mayores',
      phone: '877-477-3646',
      service: 'Protección de adultos mayores, con apoyo para familias y cuidadores.'
    }, {
      name: 'National Domestic Violence Hotline',
      phone: '800-799-7233',
      service: 'Línea nacional de violencia doméstica. 24/7.'
    }]
  }, {
    categoryEs: 'Adicción / Sustancias',
    categoryEn: 'Addiction / Substances',
    icon: ShieldAlert,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    intro: 'La recuperación es posible. Líneas confidenciales y gratuitas, sin juicios. Llama hoy.',
    introEn: 'Recovery is possible. Confidential, free, judgment-free helplines. Call today.',
    contacts: [{
      name: 'Línea del Condado de Los Ángeles',
      phone: '844-804-7500',
      service: 'Línea de ayuda para adicciones disponible 24/7. Confidencial y gratuita.'
    }, {
      name: 'SAMHSA',
      phone: '800-662-4357',
      service: 'Apoyo nacional para tratamiento de salud mental y sustancias.'
    }, {
      name: 'Phoenix House California',
      phone: '800-378-4435',
      website: 'https://phoenixhouseca.org',
      websiteLabel: 'phoenixhouseca.org',
      address: 'Lake View Terrace, CA',
      service: 'Tratamiento de adicciones.'
    }, {
      name: 'Pueblo y Salud',
      phone: '818-837-2272',
      website: 'https://pys.org',
      websiteLabel: 'pys.org',
      address: 'San Fernando, CA',
      service: 'Prevención de sustancias.'
    }]
  }, {
    categoryEs: 'Alzheimer y Demencia',
    categoryEn: "Alzheimer's & Dementia",
    icon: Stethoscope,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    intro: 'Cuidar a un ser querido con Alzheimer requiere apoyo. No tienes que hacerlo solo.',
    introEn: "Caring for a loved one with Alzheimer's requires support. You don't have to do it alone.",
    contacts: [{
      name: 'Alzheimer Los Ángeles',
      phone: '844-435-7259',
      service: 'Línea de ayuda, grupos de apoyo, recursos educativos y orientación para familias.'
    }, {
      name: "Alzheimer's Association",
      phone: '800-272-3900',
      service: 'Apoyo y recursos para Alzheimer.'
    }, {
      name: 'LA Found',
      phone: '833-569-7651',
      service: 'Registro y localización para personas con demencia que pueden extraviarse. Gratuito y confidencial.'
    }]
  }, {
    categoryEs: 'Vivienda',
    categoryEn: 'Housing',
    icon: Home,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    intro: 'Acceso a vivienda segura y asequible. Asistencia, protección de derechos y orientación para inquilinos y familias.',
    introEn: 'Access to safe, affordable housing. Assistance, rights protection, and guidance for tenants and families.',
    contacts: [{
      name: 'Programa "A Salvo en Casa"',
      phone: '213-610-1589',
      service: 'Modificaciones del hogar para mejorar seguridad y accesibilidad.'
    }, {
      name: 'Centro de Derechos de Vivienda',
      phone: '800-477-5977',
      service: 'Asesoría legal gratuita sobre derechos de inquilinos, desalojos y discriminación.'
    }, {
      name: 'Departamento de Vivienda de Los Ángeles',
      phone: '213-808-8803',
      service: 'Programas de asistencia de vivienda, subsidios y recursos.'
    }, {
      name: 'Hope the Mission',
      phone: '818-392-0020',
      website: 'https://hopethemission.org',
      websiteLabel: 'hopethemission.org',
      address: 'Northridge, CA',
      service: 'Refugio y apoyo.'
    }, {
      name: 'CHIRP Los Angeles',
      website: 'https://chirpla.org',
      websiteLabel: 'chirpla.org',
      service: 'Vivienda para personas con VIH.'
    }, {
      name: 'Habitat for Humanity Greater LA',
      phone: '310-323-4663',
      website: 'https://habitatla.org',
      websiteLabel: 'habitatla.org',
      service: 'Vivienda accesible.'
    }]
  }, {
    categoryEs: 'Servicios de Apoyo de Vida',
    categoryEn: 'Life Support Services',
    icon: LifeBuoy,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    intro: 'Programas esenciales para mantener independencia, movilidad y calidad de vida en el hogar y la comunidad.',
    introEn: 'Essential programs to maintain independence, mobility, and quality of life at home and in the community.',
    contacts: [{
      name: 'Access Services',
      phone: '800-827-0829',
      service: 'Transporte accesible para personas con discapacidades y la comunidad.'
    }, {
      name: 'Ombudsman',
      phone: '800-334-9473',
      service: 'Defensor de derechos para residentes en hogares de cuidado a largo plazo.'
    }, {
      name: 'IHSS',
      phone: '888-822-9622',
      service: 'Servicios de apoyo en el hogar para personas con discapacidades.'
    }, {
      name: 'Centros de Recursos Comunitarios',
      phone: '877-287-6290',
      service: 'Care + Blue Shield — recursos integrados de salud y apoyo comunitario.'
    }]
  }, {
    categoryEs: 'Abogacía Comunitaria',
    categoryEn: 'Community Advocacy',
    icon: Megaphone,
    color: 'text-fuchsia-600',
    bgColor: 'bg-fuchsia-50',
    borderColor: 'border-fuchsia-200',
    intro: 'Organizaciones que fortalecen los derechos, la voz y el poder de personas, familias y comunidades.',
    introEn: 'Organizations that strengthen the rights, voice, and power of people, families, and communities.',
    contacts: [{
      name: 'Legal Aid Foundation',
      service: 'Asistencia legal gratuita para comunidades de bajos ingresos.'
    }, {
      name: 'SAJE',
      service: 'Justicia económica estratégica para comunidades del sur de LA.'
    }, {
      name: 'LA Alliance for Community Health',
      service: 'Alianza para la salud comunitaria en Los Ángeles.'
    }, {
      name: 'PALA',
      service: 'Abogacía y liderazgo comunitario.'
    }, {
      name: 'Health Rights Center',
      service: 'Defensa de derechos de salud para comunidades vulnerables.'
    }, {
      name: 'Plaza Comunitaria Sinaloa',
      phone: '818-272-3638',
      service: 'Centro de recursos comunitarios.'
    }, {
      name: 'WISE & Healthy Aging',
      phone: '310-394-9871',
      service: 'Apoyo para adultos mayores y envejecimiento saludable.'
    }, {
      name: 'MOVE LA',
      service: 'Movilidad sostenible y transporte para todas las personas.'
    }, {
      name: 'TRUST South LA',
      service: 'Vivienda asequible y poder comunitario en el sur de Los Ángeles.'
    }, {
      name: 'Coalición TransLatin@',
      service: 'Apoyo e inclusión para la comunidad TransLatin@ en LA.'
    }, {
      name: 'LA Works — Voluntariado',
      service: 'Conecta voluntarios con organizaciones comunitarias de Los Ángeles.'
    }]
  }, {
    categoryEs: 'Organizaciones Basadas en Fe',
    categoryEn: 'Faith-Based Organizations',
    icon: Church,
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    intro: 'Sirven a toda la comunidad, independientemente de la afiliación religiosa. Servicios sociales, acompañamiento espiritual y recursos prácticos.',
    introEn: 'Serving the entire community regardless of religious affiliation. Social services, spiritual companionship, and practical resources.',
    contacts: [{
      name: 'Caridades Católicas',
      phone: '213-251-3400',
      service: 'Servicios sociales integrales: alimentos, vivienda, inmigración y más. Sin importar fe o procedencia.'
    }, {
      name: 'Jewish Family Service',
      phone: '818-984-1380',
      service: 'Salud mental, apoyo comunitario, asistencia para refugiados. Abiertos a personas de todas las tradiciones.'
    }]
  }, {
    categoryEs: 'Apoyo para la Comunidad',
    categoryEn: 'Community Support',
    icon: Heart,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    intro: 'Recursos para mantenerte informado, saludable y conectado. Buscar apoyo es un acto de cuidado.',
    introEn: 'Resources to stay informed, healthy, and connected. Seeking support is an act of care.',
    contacts: [{
      name: 'USC Caregiver Center',
      phone: '800-540-4442',
      service: 'Investigación y apoyo para quienes cuidan a familiares y personas de la comunidad.'
    }, {
      name: 'NCOA — National Council on Aging',
      service: 'Recursos nacionales para personas, familias y cuidadores.'
    }, {
      name: 'AARP',
      service: 'Defensa de derechos y bienestar de adultos y personas mayores.'
    }, {
      name: 'Instituto Nacional de Envejecimiento',
      service: 'Información científica y recursos sobre salud, envejecimiento y cuidado.'
    }]
  }, {
    categoryEs: 'Conectividad y Soluciones Digitales',
    categoryEn: 'Digital Connectivity Solutions',
    icon: Wifi,
    color: 'text-sky-600',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    intro: 'Teléfono e internet gratuitos o de bajo costo. Esencial para conectarte con servicios, familia y comunidad.',
    introEn: 'Free or low-cost phone and internet. Essential for connecting with services, family, and community.',
    contacts: [{
      name: 'TruConnect',
      phone: '818-259-8049',
      website: 'https://truconnect.com',
      websiteLabel: 'truconnect.com',
      email: 'jandrade@truconnect.com',
      contactPerson: 'José Andrade',
      service: 'Teléfono gratuito a través del programa Lifeline para personas que califican por ingresos.'
    }, {
      name: 'Marketing Digital y Diseño Web — Ivan Gonzalez',
      phone: '818-359-3177',
      email: 'ivancalifornia1978@gmail.com',
      contactPerson: 'Ivan Gonzalez',
      photo: 'https://cdn.magicpatterns.com/uploads/tg8f7FYtZ8NCrpFVEdCLME/imagen_facebook_ivan.png',
      service: 'Creador, diseñador y desarrollador de este portal comunitario para el Sylmar Neighborhood Council. Ofrece con humildad sus servicios profesionales bilingües: diseño de sitios web modernos, marketing digital, creación de contenido y estrategias de presencia en línea para negocios, organizaciones y proyectos comunitarios que buscan amplificar su mensaje y servir mejor a su gente.'
    }]
  }, {
    categoryEs: 'Medio Ambiente',
    categoryEn: 'Environment',
    icon: Leaf,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    intro: 'Acceso a espacios naturales y programas ambientales para la salud física y mental de la comunidad.',
    introEn: 'Access to natural spaces and environmental programs for the physical and mental health of the community.',
    contacts: [{
      name: 'Nature for All',
      phone: '323-836-2180',
      website: 'https://lanatureforall.org',
      websiteLabel: 'lanatureforall.org',
      service: 'Programas ambientales y acceso a parques.'
    }, {
      name: 'TreePeople',
      phone: '818-753-4600',
      website: 'https://treepeople.org',
      websiteLabel: 'treepeople.org',
      service: 'Educación ambiental.'
    }, {
      name: 'Pacoima Beautiful',
      phone: '818-899-2454',
      website: 'https://pacoimabeautiful.org',
      websiteLabel: 'pacoimabeautiful.org',
      address: 'Pacoima, CA',
      service: 'Justicia ambiental.'
    }, {
      name: 'Sierra Club',
      website: 'https://sierraclub.org',
      websiteLabel: 'sierraclub.org',
      service: 'Medio ambiente.'
    }, {
      name: 'GRID Alternatives',
      phone: '310-735-9762',
      website: 'https://gridalternatives.org',
      websiteLabel: 'gridalternatives.org',
      service: 'Energía solar.'
    }, {
      name: 'Los Angeles Neighborhood Land Trust',
      phone: '213-572-0188',
      website: 'https://lanlt.org',
      websiteLabel: 'lanlt.org',
      service: 'Parques y espacios verdes.'
    }]
  }, {
    categoryEs: 'Educación / Niños',
    categoryEn: 'Education / Children',
    icon: Baby,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    intro: 'La educación es la base del futuro de nuestra comunidad. Apoyo educativo y desarrollo infantil.',
    introEn: "Education is the foundation of our community's future. Educational support and child development.",
    contacts: [{
      name: 'UCLA Early Head Start',
      website: 'https://uclahealth.org',
      websiteLabel: 'uclahealth.org',
      email: 'JuliaPerez@mednet.ucla.edu',
      contactPerson: 'Julia Perez',
      service: 'Desarrollo infantil temprano para niños de 0 a 3 años y sus familias.'
    }, {
      name: 'School on Wheels',
      phone: '805-641-1678',
      website: 'https://schoolonwheels.org',
      websiteLabel: 'schoolonwheels.org',
      email: 'contact@schoolonwheels.org',
      contactPerson: 'Claudia',
      service: 'Tutoría gratuita para niños y jóvenes en situación de falta de vivienda o necesidad educativa.'
    }]
  }, {
    categoryEs: 'Servicios Financieros',
    categoryEn: 'Financial Services',
    icon: DollarSign,
    color: 'text-lime-700',
    bgColor: 'bg-lime-50',
    borderColor: 'border-lime-200',
    intro: 'Servicios financieros confiables para la estabilidad económica de las familias. Las uniones de crédito ofrecen mejores tasas que los bancos tradicionales.',
    introEn: 'Trusted financial services for family economic stability. Credit unions offer better rates than traditional banks.',
    contacts: [{
      name: 'Premier America Credit Union',
      website: 'https://premieramerica.com',
      websiteLabel: 'premieramerica.com',
      service: 'Unión de crédito comunitaria con cuentas de ahorro, préstamos y servicios bancarios accesibles para familias.'
    }]
  }, {
    categoryEs: 'Apoyo Especializado',
    categoryEn: 'Specialized Support',
    icon: Star,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    intro: 'Apoyo específico para condiciones médicas y necesidades particulares. Muchos servicios son gratuitos o de bajo costo.',
    introEn: 'Specific support for medical conditions and particular needs. Many services are free or low-cost.',
    contacts: [{
      name: "Children's Burn Foundation",
      phone: '818-676-4483',
      website: 'https://childburn.org',
      websiteLabel: 'childburn.org',
      email: 'burnprevention@childburn.org',
      contactPerson: 'Debbie',
      service: 'Apoyo integral para niños y familias afectadas por quemaduras: prevención, tratamiento y recuperación.'
    }, {
      name: 'LLS — Leukemia & Lymphoma Society',
      website: 'https://lls.org',
      websiteLabel: 'lls.org',
      contactPerson: 'Odilia Shawn',
      service: 'Apoyo, recursos e información para personas y familias que enfrentan leucemia, linfoma y otras enfermedades de la sangre.'
    }]
  }, {
    categoryEs: 'Comunidad / Desarrollo',
    categoryEn: 'Community / Development',
    icon: Building2,
    color: 'text-stone-700',
    bgColor: 'bg-stone-100',
    borderColor: 'border-stone-200',
    intro: 'Organizaciones que fortalecen el tejido social de familias, jóvenes y adultos en el Condado de LA.',
    introEn: 'Organizations that strengthen the social fabric of families, youth, and adults in LA County.',
    contacts: [{
      name: 'ONEgeneration',
      phone: '818-708-6603',
      website: 'https://onegeneration.org',
      websiteLabel: 'onegeneration.org',
      service: 'Programas intergeneracionales para mayores y familias.'
    }, {
      name: 'Generaciones en Acción',
      phone: '323-895-8749',
      website: 'https://generacionesenaccion.org',
      websiteLabel: 'generacionesenaccion.org',
      service: 'Programas comunitarios para familias y jóvenes.'
    }, {
      name: 'Pueblo y Salud',
      phone: '818-837-2272',
      website: 'https://pys.org',
      websiteLabel: 'pys.org',
      service: 'Salud comunitaria en San Fernando Valley.'
    }, {
      name: 'Visión y Compromiso',
      phone: '818-398-9111',
      website: 'https://visionycompromiso.org',
      websiteLabel: 'visionycompromiso.org',
      service: 'Talleres de liderazgo y bienestar.'
    }, {
      name: 'Chicas Mom',
      phone: '818-447-3811',
      website: 'https://chicasmom.org',
      websiteLabel: 'chicasmom.org',
      service: 'Apoyo a madres.'
    }, {
      name: 'Indigo Hands',
      website: 'https://ihands.org/home',
      websiteLabel: 'ihands.org',
      service: 'Apoyo comunitario.'
    }, {
      name: 'Corazones Unidos Foundation',
      phone: '818-568-9668',
      website: 'https://corazonesunidosfund.com/es',
      websiteLabel: 'corazonesunidosfund.com',
      service: 'Apoyo a niños y familias.'
    }, {
      name: 'ICON CDC',
      phone: '818-901-4833',
      website: 'https://iconcdc.org',
      websiteLabel: 'iconcdc.org',
      address: 'Van Nuys, CA',
      service: 'Desarrollo comunitario.'
    }, {
      name: 'Cottonwood Urban Farm',
      website: 'https://cufarm.org',
      websiteLabel: 'cufarm.org',
      address: 'San Fernando, CA',
      service: 'Agricultura urbana.'
    }, {
      name: "God's Closet — Tujunga",
      phone: '818-352-4663',
      website: 'https://godscloset.com',
      websiteLabel: 'godscloset.com',
      service: 'Apoyo con ropa.'
    }, {
      name: 'Helping Hands Resource Center',
      website: 'https://helpinghandsrc.org',
      websiteLabel: 'helpinghandsrc.org',
      service: 'Apoyo a familias.'
    }, {
      name: 'School on Wheels',
      phone: '805-641-1678',
      website: 'https://schoolonwheels.org',
      websiteLabel: 'schoolonwheels.org',
      service: 'Tutoría gratuita.'
    }, {
      name: 'Boys & Girls Club',
      website: 'https://bgca.org',
      websiteLabel: 'bgca.org',
      service: 'Programas juveniles.'
    }, {
      name: 'Scouting America',
      website: 'https://scouting.org',
      websiteLabel: 'scouting.org',
      service: 'Programas juveniles.'
    }, {
      name: "Tía Chucha's Centro Cultural",
      phone: '818-939-3433',
      website: 'https://tiachucha.org',
      websiteLabel: 'tiachucha.org',
      address: 'Sylmar, CA',
      service: 'Cultura comunitaria.'
    }, {
      name: 'Parent Pioneers',
      website: 'https://parentpioneers.org',
      websiteLabel: 'parentpioneers.org',
      service: 'Comunidad y familias.'
    }, {
      name: 'Goodwill Southern California',
      phone: '888-446-6394',
      website: 'https://goodwillsocal.org',
      websiteLabel: 'goodwillsocal.org',
      service: 'Empleo.'
    }, {
      name: 'Volunteers of America (VOALA)',
      phone: '213-389-1500',
      website: 'https://voala.org',
      websiteLabel: 'voala.org',
      service: 'Servicios sociales.'
    }]
  }, {
    categoryEs: 'Gobierno / Servicios Públicos',
    categoryEn: 'Government / Public Services',
    icon: Landmark,
    color: 'text-slate-700',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-200',
    intro: 'Servicios esenciales del Condado de Los Ángeles: registro para votar, salud pública, documentos oficiales. Son tu derecho como residente.',
    introEn: 'Essential services from LA County: voter registration, public health, official documents. They are your right as a resident.',
    contacts: [{
      name: 'Registrar-Recorder / County Clerk',
      website: 'https://lavote.gov',
      websiteLabel: 'lavote.gov',
      contactPerson: 'Yesenia Enriquez',
      address: 'Norwalk, CA',
      service: 'Registro para votar, certificados de nacimiento, matrimonio y documentos oficiales del Condado.'
    }, {
      name: 'LA County Public Health',
      phone: '888-700-9995',
      website: 'https://publichealth.lacounty.gov',
      websiteLabel: 'publichealth.lacounty.gov',
      contactPerson: 'Rafaela Peralta',
      service: 'Salud pública: vacunas, programas de prevención, información de salud y emergencias.'
    }]
  }, {
    categoryEs: 'Otros Recursos / En Verificación',
    categoryEn: 'Other Resources / Under Verification',
    icon: Info,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-300',
    intro: 'Recursos en proceso de validación. Se recomienda confirmar los datos antes de contactarlos.',
    introEn: 'Resources being validated. We recommend confirming details before contacting.',
    contacts: [{
      name: 'Indigo Hands',
      email: 'ana@ihands.org',
      service: 'En proceso de verificación de información y servicios.'
    }, {
      name: 'Chicas Mom',
      phone: '818-447-3811',
      email: 'info@chicasmom.org',
      service: 'En proceso de verificación de información y servicios.'
    }, {
      name: 'Scouting America',
      contactPerson: 'Carmen',
      service: 'En proceso de verificación. Organización de scouts con programas para jóvenes y familias.'
    }, {
      name: 'Serra Medical',
      service: 'En proceso de identificación y verificación de servicios disponibles.'
    }]
  }];
  const toggleCategory = (idx: number) => {
    setOpenCategory(openCategory === idx ? null : idx);
  };
  const verifiedCount = directory.filter(c => !c.pending).length;
  const totalContacts = directory.reduce((sum, c) => sum + c.contacts.length, 0);
  const emergencyNumbers = [{
    label: 'Emergencias',
    en: 'Emergency',
    number: '911',
    color: 'bg-red-600'
  }, {
    label: 'Crisis de Salud Mental',
    en: 'Mental Health Crisis',
    number: '988',
    color: 'bg-purple-600'
  }, {
    label: 'Violencia Doméstica',
    en: 'Domestic Violence',
    number: '1-800-799-7233',
    color: 'bg-pink-600'
  }, {
    label: 'Adicciones LA County',
    en: 'LA County Addiction',
    number: '1-844-804-7500',
    color: 'bg-orange-600'
  }, {
    label: 'Salud Mental LA',
    en: 'LA Mental Health',
    number: '800-854-7771',
    color: 'bg-indigo-600'
  }, {
    label: 'Housing Rights',
    en: 'Housing Rights',
    number: '1-800-477-5977',
    color: 'bg-blue-600'
  }];
  return <section id="recursos" className="py-20 px-4 md:px-8 bg-gradient-to-b from-white to-gray-50 border-t border-gray-200" data-id="element-288" data-mp="1u3v0owlwnj0m">
      <div className="max-w-5xl mx-auto" data-id="element-289" data-mp="1p8foe93jpv6b">
        {/* Encabezado Oficial Bilingüe */}
        <div className="text-center mb-12" data-id="element-290" data-mp="2419h4w7tw7sx">
          <div className="inline-flex items-center gap-2 bg-sylmar-light text-sylmar font-bold text-xs uppercase tracking-widest px-4 py-2 rounded-full mb-6 border border-sylmar/20" data-id="element-291" data-mp="1z3zkowm0uvvl">
            <FileText className="w-4 h-4" data-id="element-292" data-mp="4qhut2y59mxe" />
            <span data-id="element-293" data-mp="4rsx0ybxymme">Directorio Oficial · Official Directory</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-charcoal mb-2 notranslate leading-tight" data-id="element-294" data-mp="uff5xb9vr11g">
            Para la Comunidad de California
          </h2>
          <p className="text-xl text-sylmar font-bold notranslate mb-6 italic" data-id="element-295" data-mp="13f1xz6gpyuhu">
            For the California Community
          </p>
          <div className="max-w-3xl mx-auto space-y-2 mb-6" data-id="element-296" data-mp="18f6euxljhr8l">
            <p className="text-gray-700 text-base md:text-lg leading-relaxed" data-id="element-297" data-mp="1dd7ybjmvnskl">
              <strong data-id="element-298" data-mp="1db57wyhtbxfc">Lista Consolidada de Recursos Comunitarios.</strong>{' '}
              Acceso, Confianza e Inclusión para Familias, Jóvenes, Adultos y
              Personas Mayores.
            </p>
            <p className="text-gray-500 text-sm md:text-base italic leading-relaxed" data-id="element-299" data-mp="1m7mju3y03zc5">
              Consolidated List of Community Resources. Access, Trust, and
              Inclusion for Families, Youth, Adults, and Seniors.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mb-8" data-id="element-300" data-mp="213vgcxqibykv">
            <span className="bg-sylmar text-white text-xs font-bold px-3 py-1.5 rounded-full" data-id="element-301" data-mp="3lkl46yhv61nn">
              {directory.length} Categorías
            </span>
            <span className="bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1" data-id="element-302" data-mp="3gkgrljk2tr2p">
              <Award className="w-3 h-3" data-id="element-303" data-mp="36nlrwxc2hkmu" /> {verifiedCount} Verificadas
            </span>
            <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full" data-id="element-304" data-mp="36pyi1xrzb1az">
              {totalContacts} Contactos
            </span>
          </div>
          <a href="https://drive.google.com/file/d/1cpNmgKwSGrNp-64yHXAs0nFHS7xrNIcs/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-charcoal hover:bg-sylmar text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-lg hover:shadow-xl hover:-translate-y-1 group" data-id="element-305" data-mp="37dkhlqmdxw8">
            <FileText className="w-6 h-6" data-id="element-306" data-mp="m4y0027hvk46" />
            <div className="text-left" data-id="element-307" data-mp="m3wqw3a1h7p3">
              <span className="block leading-tight" data-id="element-308" data-mp="3qmhpazqtahe2">
                Ver Guía Completa en PDF
              </span>
              <span className="block text-xs font-normal opacity-80" data-id="element-309" data-mp="3vf2flraieccu">
                View Full PDF Guide
              </span>
            </div>
            <Download className="w-5 h-5 ml-2 opacity-70 group-hover:opacity-100" data-id="element-310" data-mp="3mlytyvppmt74" />
          </a>
        </div>

        {/* Números de Emergencia Destacados */}
        <div className="mb-10 bg-gradient-to-br from-red-50 via-white to-orange-50 border-2 border-red-200 rounded-3xl p-6 md:p-8 shadow-sm" data-id="element-311" data-mp="e6njg57ufab6">
          <div className="flex items-center gap-3 mb-5" data-id="element-312" data-mp="47yaj1xxd072">
            <div className="p-2.5 bg-red-600 rounded-xl text-white" data-id="element-313" data-mp="47g4xepdh9mh">
              <AlertTriangle className="w-5 h-5" data-id="element-314" data-mp="969kdco7zapa" />
            </div>
            <div data-id="element-315" data-mp="2oy6xk3yh7w0l">
              <h3 className="text-lg md:text-xl font-black text-red-700 uppercase tracking-wide" data-id="element-316" data-mp="2oxnntaizcdxn">
                Números de Emergencia
              </h3>
              <p className="text-xs font-bold text-red-500 italic" data-id="element-317" data-mp="2tv71o9brmop2">
                Emergency Hotlines · Available 24/7
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-318" data-mp="2hdw97v5u2qoj">
            {emergencyNumbers.map((em, idx) => <a key={idx} href={`tel:${em.number.replace(/\D/g, '')}`} className={`${em.color} text-white rounded-2xl p-4 hover:scale-[1.02] active:scale-[0.99] transition-transform shadow-md hover:shadow-lg flex flex-col items-start`} data-id="element-319" data-mp="2vdnvskaf1fe5" data-mpi={idx}>
                <span className="text-[10px] font-bold uppercase tracking-wider opacity-90" data-id="element-320" data-mp="1yq11ot0te93v">
                  {em.label}
                </span>
                <span className="text-[9px] italic opacity-70 mb-1" data-id="element-321" data-mp="27ocqcpi4ewto">
                  {em.en}
                </span>
                <span className="text-xl md:text-2xl font-black notranslate tracking-tight" data-id="element-322" data-mp="1jyii6l8qxwfr">
                  {em.number}
                </span>
              </a>)}
          </div>
        </div>

        {/* Acordeón de Categorías */}
        <div className="space-y-3" data-id="element-323" data-mp="d29i19t5u77u">
          {directory.map((section, idx) => {
          const isOpen = openCategory === idx;
          return <div key={idx} className={`bg-white rounded-3xl shadow-sm border-2 overflow-hidden transition-all ${isOpen ? `${section.borderColor} shadow-lg` : 'border-gray-200'}`} data-id="element-324" data-mp="3betfysfyvj8" data-mpi={idx}>
                <button onClick={() => toggleCategory(idx)} className={`w-full ${section.bgColor} p-5 flex items-center justify-between gap-4 cursor-pointer hover:brightness-95 transition-all`} data-id="element-325" data-mp="vy9b6nxvjgt0">
                  <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-326" data-mp="hzst60uka3vk">
                    <div className={`p-3 bg-white rounded-2xl shadow-sm ${section.color} flex-shrink-0`} data-id="element-327" data-mp="i1m59vny1gay">
                      <section.icon className="w-7 h-7" data-id="element-328" data-mp="32op9g06gm6h7" />
                    </div>
                    <div className="text-left flex-1 min-w-0" data-id="element-329" data-mp="1i892mxi6707z">
                      <h3 className={`text-lg md:text-xl font-black ${section.color} leading-tight`} data-id="element-330" data-mp="1i5mx13euause">
                        {section.categoryEs}
                      </h3>
                      <p className="text-xs md:text-sm font-bold text-gray-500 italic" data-id="element-331" data-mp="1vze507cxa6m0">
                        {section.categoryEn}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0" data-id="element-332" data-mp="25wiqs62uw02a">
                    <span className="hidden md:flex items-center gap-1 text-xs font-bold text-gray-500 bg-white px-3 py-1.5 rounded-full shadow-sm" data-id="element-333" data-mp="2aw56cm65pse9">
                      <span className={`${section.color} font-black`} data-id="element-334" data-mp="2ewfeo5hf8uwa">
                        {section.contacts.length}
                      </span>
                      <span data-id="element-335" data-mp="kjpjzlj8bhnu">contactos</span>
                    </span>
                    <ChevronDown className={`w-6 h-6 ${section.color} transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-336" data-mp="kjz6gz52f6os" />
                  </div>
                </button>

                <div className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-337" data-mp="1wvb9ogicllx0">
                  <div className="overflow-hidden" data-id="element-338" data-mp="1sw2f59fhhk0e">
                    {/* Intro bilingüe de la categoría (si existe) */}
                    {section.intro && <div className={`px-5 pt-5 pb-2 ${section.bgColor}/30 border-t border-gray-100`} data-id="element-339" data-mp="21tuv8ft14vou">
                        <p className="text-sm text-gray-700 leading-relaxed" data-id="element-340" data-mp="2bkfu84cm828a">
                          {section.intro}
                        </p>
                        {section.introEn && <p className="text-xs text-gray-400 italic leading-relaxed mt-1" data-id="element-341" data-mp="2kl5miy2o3ah9">
                            {section.introEn}
                          </p>}
                      </div>}

                    <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4 bg-gray-50/50" data-id="element-342" data-mp="394fr1l8xuyrh">
                      {section.contacts.map((contact, cIdx) => {
                    const hasPhone = !!contact.phone;
                    const isShortCode = hasPhone && contact.phone!.length <= 3;
                    const phoneHref = hasPhone ? isShortCode ? `tel:${contact.phone}` : `tel:${formatPhoneNumber(contact.phone!)}` : undefined;
                    const hasDetails = contact.address || contact.service || contact.website || contact.email || contact.contactPerson;
                    // Si no hay teléfono, el sitio web se convierte en el CTA principal
                    const primaryHref = phoneHref || (contact.website ? contact.website : undefined);
                    const isPrimaryPhone = !!phoneHref;
                    return <div key={cIdx} className="bg-white rounded-2xl border-2 border-gray-100 overflow-hidden hover:border-gray-300 hover:shadow-md transition-all" data-id="element-343" data-mp="1lnj1fgvd5e59" data-mpi={cIdx}>
                            {primaryHref ? <a href={primaryHref} target={isPrimaryPhone ? undefined : '_blank'} rel={isPrimaryPhone ? undefined : 'noopener noreferrer'} className="group flex items-center gap-3 p-4 bg-white hover:bg-blue-50 active:bg-blue-100 active:scale-[0.99] transition-all duration-200 cursor-pointer border-b-2 border-gray-50" aria-label={isPrimaryPhone ? `Llamar a ${contact.name} al ${contact.phone}` : `Visitar el sitio web de ${contact.name}`} data-id="element-344" data-mp="16rtad4bbmato">
                                <OrgLogo name={contact.name} website={contact.website} color={section.color} bgColor={section.bgColor} photo={contact.photo} data-id="element-345" data-mp="4y1ej6lkik3m" />

                                <div className="flex flex-col flex-1 min-w-0" data-id="element-346" data-mp="1pah4kubsjy8i">
                                  <span className="text-charcoal font-bold text-sm mb-1 leading-tight group-hover:text-blue-800 transition-colors line-clamp-2" data-id="element-347" data-mp="3le5ebrsgr4x3">
                                    {contact.name}
                                  </span>
                                  {isPrimaryPhone ? <span className="text-lg md:text-xl font-black text-blue-700 tracking-tight notranslate" data-id="element-348" data-mp="3qf1h2212wl0x">
                                      {contact.phone}
                                    </span> : <span className="text-sm font-bold text-blue-600 notranslate truncate flex items-center gap-1" data-id="element-349" data-mp="3ce8klbr7vfv6">
                                      <Globe className="w-3.5 h-3.5 flex-shrink-0" data-id="element-350" data-mp="37n8q0d5kl9vu" />
                                      {contact.websiteLabel || getDomain(contact.website) || 'Visitar sitio'}
                                    </span>}
                                </div>

                                <div className={`flex-shrink-0 w-12 h-12 ${isPrimaryPhone ? 'bg-[#28a745]' : 'bg-sylmar'} rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg group-hover:scale-110 transition-all`} data-id="element-351" data-mp="z7sfit2xlus2">
                                  {isPrimaryPhone ? <Phone className="w-6 h-6 text-white fill-current" data-id="element-352" data-mp="1d5h1y5cnd9z8" /> : <ExternalLink className="w-5 h-5 text-white" data-id="element-353" data-mp="147535g1mn5vp" />}
                                </div>
                              </a> :
                      // Sin teléfono ni sitio web — solo nombre + info
                      <div className="flex items-center gap-3 p-4 bg-white border-b-2 border-gray-50" data-id="element-354" data-mp="3twgv82i5s7x7">
                                <OrgLogo name={contact.name} website={contact.website} color={section.color} bgColor={section.bgColor} photo={contact.photo} data-id="element-355" data-mp="oho686404wlu" />
                                <div className="flex flex-col flex-1 min-w-0" data-id="element-356" data-mp="184m5jbdn1f90">
                                  <span className="text-charcoal font-bold text-sm leading-tight line-clamp-2" data-id="element-357" data-mp="186ostnneoua1">
                                    {contact.name}
                                  </span>
                                  <span className="text-xs italic text-gray-400 mt-1" data-id="element-358" data-mp="ycpy39pnwcs4">
                                    Ver detalles abajo
                                  </span>
                                </div>
                              </div>}

                            {hasDetails && <div className="p-3 space-y-2 bg-gray-50/30 text-sm" data-id="element-359" data-mp="1tg1jdtfxpwho">
                                {contact.contactPerson && <div className="flex items-start gap-2 text-gray-700" data-id="element-360" data-mp="1yeuz7sip1zl0">
                                    <span className={`text-xs font-bold uppercase tracking-wider ${section.color} mt-0.5 flex-shrink-0`} data-id="element-361" data-mp="23c4l4y6vjt43">
                                      Contacto:
                                    </span>
                                    <span className="leading-snug notranslate" data-id="element-362" data-mp="2hbw6xl3mudsn">
                                      {contact.contactPerson}
                                    </span>
                                  </div>}
                                {contact.address && <div className="flex items-start gap-2 text-gray-600" data-id="element-363" data-mp="2w0a69m1ubk4g">
                                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" data-id="element-364" data-mp="30486k02wj0z3" />
                                    <span className="notranslate leading-snug" data-id="element-365" data-mp="3giskyjun91u">
                                      {contact.address}
                                    </span>
                                  </div>}
                                {contact.service && <div className="flex items-start gap-2 text-gray-700" data-id="element-366" data-mp="hccqkmj34e06">
                                    <span className={`text-xs font-bold uppercase tracking-wider ${section.color} mt-0.5 flex-shrink-0`} data-id="element-367" data-mp="mda8cy06n615">
                                      Servicio:
                                    </span>
                                    <span className="leading-snug" data-id="element-368" data-mp="111n4buz9sovn">
                                      {contact.service}
                                    </span>
                                  </div>}
                                {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold notranslate hover:underline pt-1" data-id="element-369" data-mp="2umgqc7ojwrjl">
                                    <Mail className="w-4 h-4 flex-shrink-0" data-id="element-370" data-mp="2gmyz7lns9272" />
                                    <span className="truncate" data-id="element-371" data-mp="3dalomzmfvkb0">
                                      {contact.email}
                                    </span>
                                  </a>}
                                {contact.website && isPrimaryPhone && <a href={contact.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold notranslate hover:underline pt-1" data-id="element-372" data-mp="2zjgv8f9nzo94">
                                    <Globe className="w-4 h-4 flex-shrink-0" data-id="element-373" data-mp="jxjxfnryzyfg" />
                                    <span className="truncate" data-id="element-374" data-mp="689oyhqgvuhn">
                                      {contact.websiteLabel || contact.website}
                                    </span>
                                  </a>}
                              </div>}
                          </div>;
                  })}
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Nota Final del Documento Oficial */}
        <div className="mt-16 max-w-3xl mx-auto" data-id="element-375" data-mp="1hmwpk8h98klf">
          <div className="bg-gradient-to-br from-sylmar-light via-white to-warm/20 rounded-3xl p-8 md:p-10 border-2 border-sylmar/20 shadow-sm relative overflow-hidden" data-id="element-376" data-mp="1hn5404t0zxyq">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-sylmar/5 rounded-full" data-id="element-377" data-mp="365hqsc3tgric"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-warm/10 rounded-full" data-id="element-378" data-mp="325h09t3g0pux"></div>
            <div className="relative z-10" data-id="element-379" data-mp="3fwccnfd5hp39">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-dashed border-sylmar/30" data-id="element-380" data-mp="3fxdp9o9tejn3">
                <div className="p-2 bg-sylmar rounded-xl text-white" data-id="element-381" data-mp="3b39h51otc5kf">
                  <FileText className="w-5 h-5" data-id="element-382" data-mp="3psxlkbsajkcd" />
                </div>
                <div data-id="element-383" data-mp="3kvyp3ml5n1b0">
                  <h3 className="text-xl font-black text-sylmar uppercase tracking-wider" data-id="element-384" data-mp="3kudvq0aktoeq">
                    Nota Final
                  </h3>
                  <p className="text-xs font-bold text-gray-500 italic" data-id="element-385" data-mp="3tvu6i95azsjh">
                    Final Note · Closing Statement
                  </p>
                </div>
              </div>
              <div className="space-y-6 text-gray-700" data-id="element-386" data-mp="24j07901qsyez">
                <div data-id="element-387" data-mp="1zivxdsvcb1xq">
                  <p className="leading-relaxed text-base md:text-lg" data-id="element-388" data-mp="1zjnig9rb9h9r">
                    Esta guía comunitaria fue creada para conectar a{' '}
                    <strong className="text-sylmar" data-id="element-389" data-mp="25xt3jznx0gjj">
                      familias, jóvenes, adultos y personas mayores
                    </strong>{' '}
                    con recursos importantes en California, especialmente en el
                    Condado de Los Ángeles y el Valle de San Fernando.
                  </p>
                  <p className="text-sm italic text-gray-500 mt-2 leading-relaxed" data-id="element-390" data-mp="1i9jkh5i366w3">
                    This community guide was created to connect families, youth,
                    adults, and seniors with important resources in California,
                    especially in Los Angeles County and the San Fernando
                    Valley.
                  </p>
                </div>
                <div className="bg-warm/30 border-l-4 border-warm rounded-r-2xl p-5" data-id="element-391" data-mp="zisfsh91n4o1">
                  <p className="leading-relaxed text-base font-semibold text-charcoal" data-id="element-392" data-mp="14gk5r5q4ogpl">
                    ⚠️ La información puede cambiar con el tiempo. Se recomienda{' '}
                    <strong data-id="element-393" data-mp="ryhuyifm0hgq">llamar directamente a cada organización</strong> o
                    visitar su sitio web oficial para confirmar horarios,
                    requisitos, servicios disponibles y elegibilidad.
                  </p>
                  <p className="text-sm italic text-charcoal/70 mt-2 leading-relaxed" data-id="element-394" data-mp="hza7h2f5v6fw">
                    Information may change over time. We recommend calling each
                    organization directly or visiting their official website to
                    confirm hours, requirements, available services, and
                    eligibility.
                  </p>
                </div>
                <div className="text-center pt-2" data-id="element-395" data-mp="3vg9hhhaz897j">
                  <p className="leading-relaxed text-base md:text-lg font-bold text-sylmar" data-id="element-396" data-mp="3vddkbv6ydok5">
                    🤝 Comparte esta guía con personas que puedan necesitar
                    apoyo.
                  </p>
                  <p className="leading-relaxed text-base text-gray-700 mt-1" data-id="element-397" data-mp="3btof5jjrkan6">
                    El acceso a información clara puede hacer una{' '}
                    <strong data-id="element-398" data-mp="3gu2mt0hgdiko">gran diferencia</strong> para nuestra comunidad.
                  </p>
                  <p className="text-sm italic text-gray-500 mt-3 leading-relaxed" data-id="element-399" data-mp="32ub2yj4997yr">
                    Share this guide with people who may need support. Access to
                    clear information can make a big difference for our
                    community.
                  </p>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t-2 border-dashed border-sylmar/30 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-400" data-mp="3ffr8os6s81c">
                <div className="text-center sm:text-left" data-id="element-401" data-mp="3qpnjqk7nzsnk">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-widest" data-id="element-402" data-mp="iavqr9bjcw54">
                    Documento Oficial
                  </p>
                  <p className="text-sm font-black text-charcoal notranslate" data-id="element-403" data-mp="n2drpxdk6wq3">
                    Sylmar Neighborhood Council
                  </p>
                </div>
                <div className="flex items-center gap-2 bg-sylmar text-white px-4 py-2 rounded-full" data-id="element-404" data-mp="sfc703sxgf9g">
                  <Award className="w-4 h-4" data-id="element-405" data-mp="166gy2hi8bz86" />
                  <span className="text-xs font-bold uppercase tracking-wider" data-id="element-406" data-mp="165fpcjoorotm">
                    Para la Comunidad
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}